import * as yup from "yup";
import { createAddressFields } from "../constants";
import { SelectOption, type FormSectionConfig, type TaxFormConfig } from "../types";

export const W8EXP_ORG_TYPE_OPTIONS: SelectOption[] = [
  { value: "foreign_government", label: "Foreign government" },
  { value: "international_organization", label: "International organization" },
  {
    value: "foreign_central_bank_of_issue",
    label: "Foreign central bank of issue (not wholly owned by the foreign sovereign)",
  },
  { value: "tax_exempt_organization", label: "Foreign tax-exempt organization" },
  { value: "foreign_private_foundation", label: "Foreign private foundation" },
  { value: "government_of_us_territory", label: "Government of a U.S. territory" },
  {
    value: "withholding_qualified_holder_section_1445",
    label: "Withholding qualified holder under section 1445",
  },
];

// W-8EXP Chapter 4 Status Options
export const W8EXP_CHAPTER_4_STATUS_OPTIONS: SelectOption[] = [
  { value: "participating_ffi", label: "Participating FFI" },
  { value: "reporting_model_1_ffi", label: "Reporting Model 1 FFI" },
  { value: "reporting_model_2_ffi", label: "Reporting Model 2 FFI" },
  { value: "registered_deemed_compliant_ffi", label: "Registered deemed-compliant FFI" },
  { value: "nonreporting_iga_ffi", label: "Nonreporting IGA FFI" },
  { value: "territory_financial_institution", label: "Territory financial institution" },
  { value: "international_organization", label: "International organization" },
  {
    value: "foreign_government_territory_or_central_bank",
    label:
      "Foreign government, Government of a U.S. territory or Foreign central bank of issue",
  },
  {
    value: "exempt_retirement_plan_foreign_government",
    label: "Exempt retirement plan of foreign government",
  },
  { value: "section_501_c_organization", label: "501(c) organization" },
  { value: "passive_nffe", label: "Passive NFFE" },
  { value: "direct_reporting_nffe", label: "Direct reporting NFFE" },
  { value: "sponsored_direct_reporting_nffe", label: "Sponsored direct reporting NFFE" },
];

const sections: FormSectionConfig[] = [
  {
    id: "identification",
    title: "Identification of Beneficial Owner",
    fields: [
      {
        name: "entityName",
        type: "text",
        label: "Entity name",
        placeholder: "",
        required: true,
        gridColumn: "half",
      },
      {
        name: "countryOfIncorporation",
        type: "country",
        label: "Country of incorporation",
        placeholder: "Select a country",
        required: true,
        gridColumn: "half",
      },
      {
        name: "orgType",
        type: "select",
        label: "Org type",
        placeholder: "Select a type",
        required: true,
        gridColumn: "full",
        options: W8EXP_ORG_TYPE_OPTIONS,
      },
      {
        name: "chapter4Status",
        type: "select",
        label: "Chapter 4 status (FATCA)",
        placeholder: "Select a status",
        required: true,
        gridColumn: "full",
        options: W8EXP_CHAPTER_4_STATUS_OPTIONS,
      },
      {
        name: "_permanentAddressLabel",
        type: "label",
        label: "Permanent residence address",
        required: true,
        linkedField: "permanentAddress",
        gridColumn: "full",
      },
      ...createAddressFields("permanentAddress", {
        helpText:
          "Do not use a P.O. box or in-care-of address (other than a registered address)",
      }),
      {
        name: "usTin",
        type: "text",
        label: "US TIN",
        gridColumn: "half",
      },
      {
        name: "_usTinSpacer",
        type: "label",
        label: "",
        gridColumn: "half",
      },
      {
        name: "giin",
        type: "giin",
        label: "GIIN",
        tooltip: {
          content:
            "GIIN is a 19-character identifier (including dots) and cannot contain the letter 'O'.",
        },
        gridColumn: "half",
      },
      {
        name: "foreignTin",
        type: "text",
        label: "Foreign TIN",
        gridColumn: "half",
      },
      {
        name: "giinVerified",
        type: "checkbox",
        label: "Check if GIIN has been verified through the IRS portal",
        gridColumn: "full",
      },
    ],
  },
];

const validationSchema = yup.object().shape({
  entityName: yup.string().required("Entity name is required"),
  countryOfIncorporation: yup.string().required("Country of incorporation is required"),
  orgType: yup.string().required("Org type is required"),
  chapter4Status: yup.string().required("Chapter 4 status is required"),
  permanentAddress: yup.object().shape({
    country: yup.string().required("Country is required"),
    streetLine1: yup.string().required("Street (line 1) is required"),
    city: yup.string().required("City is required"),
    zipCode: yup.string().required("Zip code is required"),
  }),
  giinVerified: yup.boolean().when("giin", {
    is: (giin: string) => Boolean(giin?.trim()),
    then: (schema) =>
      schema.test("giin-validation", "", function (value) {
        const { giin } = this.parent as { giin: string };
        if (giin?.trim() && giin.replace(/\./g, "").length !== 16) {
          return this.createError({ message: "GIIN provided is incomplete" });
        }
        if (value !== true) {
          return this.createError({
            message: "GIIN provided must be verified through the IRS portal",
          });
        }
        return true;
      }),
  }),
});

const initialValues = {
  entityName: "",
  countryOfIncorporation: "",
  orgType: "",
  chapter4Status: "",
  permanentAddress: {
    country: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    stateOrProvince: "",
    zipCode: "",
  },
  usTin: "",
  giin: "",
  foreignTin: "",
  giinVerified: false,
};

export const w8expConfig: TaxFormConfig = {
  formId: "W8EXP",
  title: "New FATCA details",
  subtitle: "W-8EXP",
  sections,
  validationSchema,
  initialValues,
};
